/*
 * Base class of <cart-drawer> and <cart-page>: the quantity steppers of
 * snippets/cart-line.liquid upgraded to /cart/change.js with `sections`.
 * Changes address lines by key and run one at a time; only the last one
 * renders, and Checkout waits until none are pending. Markup that cannot be
 * rendered is re-read through the Section Rendering API, then from the cart
 * page itself; a change is never repeated. Config comes from data-section,
 * data-change-url, data-sections-url, data-cart-url, data-item-count and the
 * data-*-error messages on the element. Subclasses may narrow parse(html) and
 * override replace(fresh) and restoreFocus(target).
 * Both cart sections include this file; the guard makes a second copy a no-op.
 */
if (!window.AsansaraCartLines) {
  window.AsansaraCartLines = class CartLines extends HTMLElement {
    queue = Promise.resolve();
    pending = 0;
    waiting = new Map();
    timer = null;
    typedInput = null;
    error = '';
    stale = false;

    connectedCallback() {
      this.setAttribute('data-js', '');
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.timer = null;
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('submit', this.onSubmit);
    }

    // Checkout and native form submits wait while a change is queued, typed or unrendered.
    get blocked() {
      return this.pending > 0 || this.timer !== null || this.stale;
    }

    // The running step is the only outstanding work, so its result is the current cart.
    get lastStep() {
      return this.pending === 1 && this.timer === null;
    }

    onClick = (event) => {
      const control = event.target.closest('[data-qty-minus], [data-qty-plus], [data-remove]');
      if (!control) return;
      const input = control.closest('[data-line-key]').querySelector('[data-qty-input]');
      if (control.hasAttribute('data-remove')) {
        event.preventDefault();
        input.value = 0;
      } else {
        const delta = control.hasAttribute('data-qty-minus') ? -1 : 1;
        input.value = Math.max(0, Number(input.value) + delta);
      }
      this.flush();
      this.change(input);
    };

    // A typed quantity waits briefly; a click or an edit on another line sends it first.
    onChange = (event) => {
      if (!event.target.matches('[data-qty-input]')) return;
      if (this.typedInput !== event.target) this.flush();
      clearTimeout(this.timer);
      this.typedInput = event.target;
      this.timer = setTimeout(() => this.flush(), 250);
      this.sync();
    };

    onSubmit = (event) => {
      if (this.blocked) event.preventDefault();
    };

    flush() {
      if (this.timer === null) return;
      clearTimeout(this.timer);
      this.timer = null;
      this.change(this.typedInput);
    }

    // A line that is already waiting is not queued twice: its step reads the input when it
    // runs, so rapid steps on one line collapse into a single request for the latest value.
    change(input) {
      const key = input.closest('[data-line-key]').dataset.lineKey;
      const queued = this.waiting.has(key);
      this.waiting.set(key, input);
      if (!queued) this.enqueue(() => this.send(key));
    }

    enqueue(step) {
      this.pending += 1;
      this.sync();
      // A step that throws must not stall the ones queued behind it.
      this.queue = this.queue.catch(() => {}).then(() => this.run(step));
    }

    async run(step) {
      try {
        const fresh = await step();
        if (this.lastStep) await this.settle(fresh);
      } finally {
        this.pending -= 1;
        this.sync();
      }
    }

    // Resolves to the fresh element, or null when the change failed or its response carried
    // no usable markup; settle() then re-reads instead of repeating the change.
    async send(key) {
      const input = this.waiting.get(key);
      this.waiting.delete(key);
      const sectionId = this.dataset.section;
      let message = this.dataset.updateError;
      try {
        const response = await fetch(this.dataset.changeUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity: Math.max(0, Number(input.value) || 0),
            sections: [sectionId],
            sections_url: this.dataset.sectionsUrl,
          }),
        });
        const data = await response.json().catch(() => null);
        if (response.ok) return this.parse(data?.sections?.[sectionId]);
        message = data?.description || data?.message || message;
      } catch {
        // Network failure: the change may or may not have applied, so the re-read decides.
      }
      this.error = message;
      return null;
    }

    async settle(fresh) {
      let reached = true;
      if (!fresh) {
        try {
          fresh = await this.load();
        } catch {
          reached = false;
        }
        if (!this.lastStep) return;
      }
      const error = this.error;
      this.error = '';
      if (fresh) {
        this.swap(fresh);
        if (error) this.showError(error);
        return;
      }
      this.stale = true;
      this.showError(this.dataset.refreshError);
      // The cart page renders the cart server-side. Offline, navigating would only trade this
      // page for a browser error page, so the message asks for a reload instead.
      if (reached) window.location.assign(this.dataset.cartUrl);
    }

    async load() {
      const sectionId = this.dataset.section;
      const url = new URL(this.dataset.sectionsUrl, window.location.origin);
      url.searchParams.set('sections', sectionId);
      const response = await fetch(url, { cache: 'no-store' });
      const data = await response.json().catch(() => null);
      return response.ok ? this.parse(data?.[sectionId]) : null;
    }

    parse(html) {
      return html ? new DOMParser().parseFromString(html, 'text/html').querySelector(this.localName) : null;
    }

    swap(fresh) {
      const active = document.activeElement;
      const key = this.contains(active) ? active.closest('[data-line-key]')?.dataset.lineKey : null;
      const hook = key && ['data-qty-minus', 'data-qty-plus', 'data-qty-input'].find((a) => active.hasAttribute(a));

      this.replace(fresh);
      this.dataset.itemCount = fresh.dataset.itemCount || '0';
      this.stale = false;
      this.restoreFocus(hook ? this.line(key)?.querySelector(`[${hook}]`) : null);
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = this.dataset.itemCount;
        el.setAttribute('data-cart-count', this.dataset.itemCount);
      });
    }

    replace(fresh) {
      this.replaceChildren(...fresh.childNodes);
    }

    // `target` is the control that had focus, found again on the same line after the swap.
    restoreFocus(target) {
      target?.focus();
    }

    line(key) {
      return [...this.querySelectorAll('[data-line-key]')].find((line) => line.dataset.lineKey === key);
    }

    sync() {
      if (this.pending) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');
      this.querySelectorAll('[name="checkout"]').forEach((button) => {
        if (this.blocked) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      });
    }

    showError(message) {
      const region = this.querySelector('[data-cart-error]');
      if (!region) return;
      region.textContent = message;
      region.hidden = false;
    }
  };
}
